"use client"

import { type ChangeEvent, useRef, useState } from "react"
import { ChevronDown, FolderOpen } from "lucide-react"

import { SituationSetupData } from "@/app/types"
import { cn } from "@/lib/utils"

type GameSetupModalProps = {
  open: boolean
  titleLabel?: string
  value: SituationSetupData
  fileName?: string
  onFileNameChange?: (name: string) => void
  onChange: (value: SituationSetupData) => void
  onCancel: () => void
  onConfirm: () => void
}

const SUBJECT_OPTIONS = ["ภาษาไทย", "คณิตศาตร์"]
const TOPIC_OPTIONS = ["ห นำ", "สระ"]
const GRADE_OPTIONS = ["ป1", "ป2", "ป3", "ป4", "ป5", "ป6"]

type SelectFieldProps = {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
}

function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-[#4b4b4b]">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-full appearance-none rounded-xl border border-[#8f8f8f] bg-[#f6f6f6] px-4 pr-10 text-sm font-semibold text-[#232323] outline-none"
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[#595959]" />
      </div>
    </div>
  )
}

export default function GameSetupModal({
  open,
  titleLabel = "",
  value,
  fileName,
  onFileNameChange,
  onChange,
  onCancel,
  onConfirm,
}: GameSetupModalProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleConfirm = () => {
    setIsSubmitted(true)
    if (!value.title.trim() || !value.backgroundImage) {
      return
    }
    onConfirm()
  }

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      onChange({
        ...value,
        backgroundImage: typeof reader.result === "string" ? reader.result : null,
      })
      onFileNameChange?.(file.name)
    }
    reader.readAsDataURL(file)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
      <div className="w-full max-w-[520px] rounded-xl bg-[#e7e7e7] px-10 pb-8 pt-8 shadow-2xl">
        <div className="mx-auto mb-7 flex h-[48px] w-[256px] items-center justify-center rounded-[5px] bg-[#FDEF5C] shadow-[0px_5px_0px_#4E4E4E]">
          <p className="text-lg font-semibold leading-none text-[#2B2B2B]">
            {titleLabel}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[#4b4b4b]">ชื่อ</label>
            <input
              type="text"
              placeholder="กรอกชื่อ..."
              value={value.title}
              onChange={(event) => onChange({ ...value, title: event.target.value })}
              className={cn(
                "h-10 w-full rounded-xl border bg-[#f6f6f6] px-4 text-sm font-semibold text-[#0ea5e9] outline-none placeholder:text-gray-400",
                isSubmitted && !value.title.trim() ? "border-red-500" : "border-[#8f8f8f]"
              )}
            />
            {isSubmitted && !value.title.trim() && (
              <p className="mt-1 text-[11px] font-medium text-red-500">กรุณากรอกชื่อ</p>
            )}
          </div>

          <SelectField
            label="เลือกหมวดหมู่ / วิชา"
            value={value.subject ?? SUBJECT_OPTIONS[0]}
            options={SUBJECT_OPTIONS}
            onChange={(subject) => onChange({ ...value, subject })}
          />

          <SelectField
            label="เลือกกลุ่ม / หัวข้อ"
            value={value.topic ?? TOPIC_OPTIONS[0]}
            options={TOPIC_OPTIONS}
            onChange={(topic) => onChange({ ...value, topic })}
          />

          <SelectField
            label="ชั้น"
            value={value.grade ?? GRADE_OPTIONS[0]}
            options={GRADE_OPTIONS}
            onChange={(grade) => onChange({ ...value, grade })}
          />

          <div>
            <label className="mb-2 block text-sm font-semibold text-[#4b4b4b]">
              ภาพ thumbnail / recommend 480x340
            </label>
            <div
              className={cn(
                "flex h-10 items-center gap-2 rounded-xl border bg-[#f6f6f6] px-4 cursor-pointer",
                isSubmitted && !value.backgroundImage ? "border-red-500" : "border-[#8f8f8f]"
              )}
              onClick={() => fileRef.current?.click()}
            >
              <input
                readOnly
                placeholder="คลิกเพื่อเลือกไฟล์..."
                value={fileName || (value.backgroundImage ? "เลือกรูปแล้ว" : "")}
                className="h-full flex-1 bg-transparent text-xs text-[#222] outline-none cursor-pointer"
              />
              <button
                type="button"
                className="inline-flex size-6 items-center justify-center rounded-md text-[#333] hover:bg-[#ececec]"
              >
                <FolderOpen className="size-4" />
              </button>
            </div>
            {isSubmitted && !value.backgroundImage && (
              <p className="mt-1 text-[11px] font-medium text-red-500">กรุณาอัพโหลดรูปภาพ</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={onCancel}
            className="h-10 min-w-[150px] rounded-xl bg-[#f1e450] px-8 text-base font-bold text-[#222] shadow-[0_2px_0_0_#c4b738] hover:brightness-95"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="h-10 min-w-[150px] rounded-xl bg-[#f1e450] px-8 text-base font-bold text-[#222] shadow-[0_2px_0_0_#c4b738] hover:brightness-95"
          >
            ตกลง
          </button>
        </div>
      </div>
    </div>
  )
}
